import React, { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddFieldModal from './AddFieldModal';
import ProjectionField from './Projectionfields';
import CityDetails from './CityDetails ';
import { getAllCity } from '../../../FeatureRedux/citydetailReducer/getCityDetail';
import { addcity } from '../../../FeatureRedux/citydetailReducer/Addcity';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import axiosInstance from '../../axiosInstance';

const MainPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sections, setSections] = useState([]);
  const [cards, setCards] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const [cityData, setCityData] = useState({
    cityname: "",
    country: "",
    state: "",
    latitude: "",
    longitude: "",
    imageUrl: "",
    description: ""
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const isAddingNewCity = location.state?.isAddingNewCity || false;
  const cityDetails = useSelector(state => state.citydetail);

  // Load city data
  useEffect(() => {
    if (!isAddingNewCity) {
      const cityname = "Lucknow";
      const state = "Uttar Pradesh";
      const country = "India";
      dispatch(getAllCity({ cityname, state, country }));
    } else {
      setIsLoading(false);
    }
  }, [dispatch, isAddingNewCity]);

  // Process loaded data
  useEffect(() => {
    if (!isAddingNewCity && cityDetails?.data) {
      const { data } = cityDetails;
      
      setCityData({
        cityname: data.city?.cityname || "",
        country: data.city?.country || "",
        state: data.city?.state || "",
        latitude: data.city?.latitude?.toString() || "",
        longitude: data.city?.longitude?.toString() || "",
        imageUrl: data.city?.imageUrl || "",
        description: data.city?.description || ""
      });

      const sectionNames = [
        'History', 'Transport', 'Social Impact', 
        'Population', 'Culture', 'Economy', 
        'Technology', 'Infrastructure', 'Public'
      ];
      
      const newSections = [];
      const newCards = {};

      sectionNames.forEach(section => {
        const sectionKey = section.toLowerCase().replace(' ', '');
        if (data[sectionKey]?.length > 0) {
          newSections.push(section);
          
          newCards[section] = data[sectionKey].map((item, index) => ({
            id: item._id || `${Date.now()}-${index}`,
            isEditing: false,
            imageUrl: item.imageUrl || "",
            ...getSectionFields(section, item)
          }));
        }
      });

      setSections(newSections);
      setCards(newCards);
      setIsLoading(false);
    }
  }, [cityDetails, isAddingNewCity]);

  // Helper function to get section-specific fields
  const getSectionFields = (section, item) => {
    switch(section) {
      case 'History': return { title: item.title || "", content: item.content || "" };
      case 'Transport': return { type: item.type || "", details: item.details || "" };
      case 'Social Impact': return { aspect: item.aspect || "", impact: item.impact || "" };
      case 'Population': return { 
        year: item.year?.toString() || "", 
        count: item.count?.toString() || "", 
        notes: item.notes || "" 
      };
      case 'Culture': return { aspect: item.aspect || "", details: item.details || "" };
      case 'Economy': return { sector: item.sector || "", info: item.info || "" };
      case 'Technology': return { field: item.field || "", description: item.description || "" };
      case 'Infrastructure': return { field: item.field || "", description: item.description || "" };
      case 'Public': return { field: item.field || "", description: item.description || "" };
      default: return {};
    }
  };

  // Reset form completely
  const resetForm = () => {
    setCityData({
      cityname: "",
      country: "",
      state: "",
      latitude: "",
      longitude: "",
      imageUrl: "",
      description: ""
    });
    
    if (isAddingNewCity) {
      setSections([]);
      setCards({});
    } else {
      dispatch(getAllCity({ 
        cityname: "Lucknow", 
        state: "Uttar Pradesh", 
        country: "India" 
      }));
    }
  };

  // Modal handlers
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // Add new fields/sections
  const handleAddFields = (selectedFields) => {
    const newSections = [...new Set([...sections, ...selectedFields])];
    const newCards = { ...cards };

    selectedFields.forEach(field => {
      if (!cards[field]) {
        newCards[field] = [createNewCard(field)];
      }
    });

    setSections(newSections);
    setCards(newCards);
    toast.success('Fields added successfully!');
  };

  // Create new card with proper fields
  const createNewCard = (section) => {
    const baseCard = { 
      id: `${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      isEditing: true,
      imageUrl: "" 
    };

    switch(section) {
      case 'History': return { ...baseCard, title: "", content: "" };
      case 'Transport': return { ...baseCard, type: "", details: "" };
      case 'Social Impact': return { ...baseCard, aspect: "", impact: "" };
      case 'Population': return { ...baseCard, year: "", count: "", notes: "" };
      case 'Culture': return { ...baseCard, aspect: "", details: "" };
      case 'Economy': return { ...baseCard, sector: "", info: "" };
      case 'Technology': return { ...baseCard, field: "", description: "" };
      case 'Infrastructure': return { ...baseCard, field: "", description: "" };
      case 'Public': return { ...baseCard, field: "", description: "" };
      default: return baseCard;
    }
  };

  // Validate all form data
  const validateForm = () => {
    // Validate city data
    const requiredCityFields = ['cityname', 'country', 'state', 'description'];
    const missingCityFields = requiredCityFields.filter(field => !cityData[field]);
    
    if (missingCityFields.length > 0) {
      toast.error(`Please fill all required city details`);
      return false;
    }

    // Validate sections
    for (const section of sections) {
      if (!cards[section]?.length) {
        toast.error(`Please add at least one item to ${section} section`);
        return false;
      }

      for (const item of cards[section]) {
        const errors = validateSectionItem(section, item);
        if (Object.keys(errors).length > 0) {
          toast.error(`Please fill all required fields in ${section} section`);
          return false;
        }
      }
    }

    return true;
  };

  // Validate section item
  const validateSectionItem = (section, item) => {
    const errors = {};
    
    switch(section) {
      case 'History':
        if (!item.title) errors.title = true;
        if (!item.content) errors.content = true;
        break;
      case 'Transport':
        if (!item.type) errors.type = true;
        if (!item.details) errors.details = true;
        break;
      case 'Social Impact':
        if (!item.aspect) errors.aspect = true;
        if (!item.impact) errors.impact = true;
        break;
      case 'Population':
        if (!item.year) errors.year = true;
        if (!item.count) errors.count = true;
        break;
      case 'Culture':
        if (!item.aspect) errors.aspect = true;
        if (!item.details) errors.details = true;
        break;
      case 'Economy':
        if (!item.sector) errors.sector = true;
        if (!item.info) errors.info = true;
        break;
      case 'Technology':
      case 'Infrastructure':
      case 'Public':
        if (!item.field) errors.field = true;
        if (!item.description) errors.description = true;
        break;
    }

    return errors;
  };

  // Submit all data
  const handleSubmitAll = async () => {
    if (!validateForm()) return;

    const finalData = {
      city: {
        ...cityData,
        latitude: cityData.latitude ? parseFloat(cityData.latitude) : 0,
        longitude: cityData.longitude ? parseFloat(cityData.longitude) : 0
      }
    };

    // Add all sections
    sections.forEach(section => {
      const sectionKey = section.toLowerCase().replace(' ', '');
      finalData[sectionKey] = cards[section].map(item => ({
        imageUrl: item.imageUrl || "",
        ...getSectionData(section, item)
      }));
    });

    try {
      const endpoint = isAddingNewCity 
        ? '/citydetail/addcitydetail' 
        : '/citydetail/updatecitydetail';
      
      const method = isAddingNewCity ? 'post' : 'put';
      
      await axiosInstance[method](endpoint, finalData);
      toast.success(`City ${isAddingNewCity ? 'added' : 'updated'} successfully!`);
      resetForm();
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.response?.data?.message || 'Error submitting data');
    }
  };

  // Get section data for submission
  const getSectionData = (section, item) => {
    switch(section) {
      case 'History': return { title: item.title, content: item.content };
      case 'Transport': return { type: item.type, details: item.details };
      case 'Social Impact': return { aspect: item.aspect, impact: item.impact };
      case 'Population': return { 
        year: parseInt(item.year) || 0, 
        count: parseInt(item.count) || 0, 
        notes: item.notes 
      };
      case 'Culture': return { aspect: item.aspect, details: item.details };
      case 'Economy': return { sector: item.sector, info: item.info };
      default: return { 
        field: item.field, 
        description: item.description 
      };
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 relative bg-gray-900 flex items-center justify-center h-[80vh] w-full">
        <div className="text-white">Loading city data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 relative bg-gray-900 overflow-y-scroll h-[80vh] w-full">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <AddFieldModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onAddFields={handleAddFields}
      />

      <CityDetails 
        cityData={cityData}
        setCityData={setCityData}
        isAddingNewCity={isAddingNewCity}
      />

      <ProjectionField 
        sections={sections}
        cards={cards}
        setCards={setCards}
        setSections={setSections}
        openModal={openModal}
        handleSubmitAll={handleSubmitAll}
      />
    </div>
  );
};

export default MainPage;

// MainPage.js
// import React, { useEffect, useState } from 'react';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import AddFieldModal from './AddFieldModal';
// import ProjectionField from './Projectionfields';
// import CityDetails from './CityDetails ';
// import { getAllCity } from '../../../FeatureRedux/citydetailReducer/getCityDetail';
// import { addcity } from '../../../FeatureRedux/citydetailReducer/Addcity';
// import { useDispatch, useSelector } from 'react-redux';
// import { useLocation } from 'react-router-dom';
// import axiosInstance from '../../axiosInstance';

// const MainPage = ({}) => {
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [sections, setSections] = useState([]);
//   const [cards, setCards] = useState({});

// const [cityData, setCityData] = useState({
  
//   city: {
//     cityname: "",
//     country: "",
//     state: "",
//     latitude: null,
//     longitude: null,
//     imageUrl: "",
//     description: ""
//   },
//   history: [
//     {
//       title: "",
//       content: "",
//       imageUrl: "",
//       _id: {}
//     }
//   ],
//   transport: [
//     {
//       type: "",
//       details: "",
//       imageUrl: "",
//       _id: {}
//     }
//   ],
//   socialImpact: [
//     {
//       aspect: "",
//       impact: "",
//       imageUrl: "",
//       _id: {}
//     }
//   ],
//   population: [
//     {
//       year: null,
//       count: null,
//       notes: "",
//       imageUrl: "",
//       _id: {}
//     }
//   ],
//   culture: [
//     {
//       aspect: "",
//       details: "",
//       imageUrl: "",
//       _id: {}
//     }
//   ],
//   economy: [
//     {
//       sector: "",
//       info: "",
//       imageUrl: "",
//       _id: {}
//     }
//   ],
//   technology: [
//     {
//       field: "",
//       description: "",
//       imageUrl: "",
//       _id: {}
//     }
//   ],
// });

//   const dispatch = useDispatch();
//   const location = useLocation();
//   const isAddingNewCity = location.state?.isAddingNewCity || false;

//   useEffect(() => {
//     if (!isAddingNewCity) {
//       const cityname = "Lucknow";
//       const state = "Uttar Pradesh";
//       const country = "India";
//       dispatch(getAllCity({ cityname, state, country }));
//     }
//   }, [dispatch, isAddingNewCity]);

//   const openModal = () => setIsModalOpen(true);
//   const closeModal = () => setIsModalOpen(false);

//   const handleAddFields = (selectedFields) => {
//     const newSections = [...sections];
//     const newCards = { ...cards };

//     selectedFields.forEach((field) => {
//       if (!sections.includes(field)) {
//         newSections.push(field);
        
//         const newCard = { id: Date.now(), isEditing: true };
        
//         switch(field) {
//           case 'History':
//             newCard.title = "";
//             newCard.content = "";
//             newCard.imageUrl = "";
//             break;
//           case 'Transport':
//             newCard.type = "";
//             newCard.details = "";
//             newCard.imageUrl = "";
//             break;
//           case 'Social Impact':
//             newCard.aspect = "";
//             newCard.impact = "";
//             newCard.imageUrl = "";
//             break;
//           case 'Population':
//             newCard.year = "";
//             newCard.count = "";
//             newCard.notes = "";
//             newCard.imageUrl = "";
//             break;
//           case 'Culture':
//             newCard.aspect = "";
//             newCard.details = "";
//             newCard.imageUrl = "";
//             break;
//           case 'Economy':
//             newCard.sector = "";
//             newCard.info = "";
//             newCard.imageUrl = "";
//             break;
//           case 'Technology':
//             newCard.field = "";
//             newCard.description = "";
//             newCard.imageUrl = "";
//             break;
//           case 'Infrastructure':
//             newCard.field = "";
//             newCard.description = "";
//             newCard.imageUrl = "";
//             break;
//           default:
//             break;
//         }
        
//         newCards[field] = [newCard];
//       }
//     });

//     setSections(newSections);
//     setCards(newCards);
//     toast.success('Fields added successfully!');
//   };

//   const handleSubmitAll = async () => {
//     // Validate city data first
//     if (!cityData.cityname || !cityData.country || !cityData.state || !cityData.description) {
//       toast.error('Please fill all required city details');
//       return;
//     }

//     // Validate sections data if not adding a new city
//     if (!isAddingNewCity) {
//       let hasErrors = false;
//       sections.forEach(section => {
//         if (!cards[section] || cards[section].length === 0) {
//           toast.error(`Please add at least one item to ${section} section`);
//           hasErrors = true;
//           return;
//         }

//         cards[section].forEach(item => {
//           if (!item.title && !item.type && !item.aspect && !item.sector && !item.field) {
//             toast.error(`Please fill all required fields in ${section} section`);
//             hasErrors = true;
//           }
//         });
//       });

//       if (hasErrors) return;
//     }

//     // Prepare final data structure
//     const finalData = {
//       city: {
//         cityname: cityData.cityname,
//         country: cityData.country,
//         state: cityData.state,
//         latitude: parseFloat(cityData.latitude) || 0,
//         longitude: parseFloat(cityData.longitude) || 0,
//         imageUrl: cityData.imageUrl,
//         description: cityData.description
//       }
//     };

//     // Add all sections to final data if not adding a new city
//     if (!isAddingNewCity) {
//       sections.forEach(section => {
//         finalData[section.toLowerCase().replace(' ', '')] = cards[section].map(item => {
//           const baseItem = {
//             imageUrl: item.imageUrl || ""
//           };

//           switch(section) {
//             case 'History':
//               return {
//                 ...baseItem,
//                 title: item.title,
//                 content: item.content
//               };
//             case 'Transport':
//               return {
//                 ...baseItem,
//                 type: item.type,
//                 details: item.details
//               };
//             case 'Social Impact':
//               return {
//                 ...baseItem,
//                 aspect: item.aspect,
//                 impact: item.impact
//               };
//             case 'Population':
//               return {
//                 ...baseItem,
//                 year: parseInt(item.year) || 0,
//                 count: parseInt(item.count) || 0,
//                 notes: item.notes
//               };
//             case 'Culture':
//               return {
//                 ...baseItem,
//                 aspect: item.aspect,
//                 details: item.details
//               };
//             case 'Economy':
//               return {
//                 ...baseItem,
//                 sector: item.sector,
//                 info: item.info
//               };
//             case 'Technology':
//               return {
//                 ...baseItem,
//                 field: item.field,
//                 description: item.description
//               };
//             case 'Infrastructure':
//               return {
//                 ...baseItem,
//                 field: item.field,
//                 description: item.description
//               };
//             default:
//               return baseItem;
//           }
//         }); 
//       });
//     }
//     try {
//       if (isAddingNewCity) {
//         // API call to add new city only
//         await axiosInstance.post('/citydetail/addcitydetail', finalData);
//         toast.success('City added successfully!');
//       } else {
//         // Existing logic for full submission
//         console.log("Final data:", finalData);
//         toast.success('Data submitted successfully!');
//       }
//     } catch (error) {
//       console.error("Error submitting data:", error);
//       toast.error('Error submitting data');
//     }
//   };

//   return (
//     <div className="p-6 relative bg-gray-900" style={{
//       overflowY: 'scroll',
//       height: '80vh',
//       width: '100%',
//       scrollbarWidth: 'px',
//       msOverflowStyle: 'none'
//     }}>
//       <ToastContainer position="top-right" autoClose={3000} />
      
//       <AddFieldModal
//         isOpen={isModalOpen}
//         onClose={closeModal}
//         onAddFields={handleAddFields}
//       />

//       <CityDetails 
//         cityData={cityData}
//         setCityData={setCityData}
//         isAddingNewCity={isAddingNewCity}
//       />

    
//         <ProjectionField 
//           sections={sections}
//           cards={cards}
//           setCards={setCards}
//           setSections={setSections}
//           openModal={openModal}
//           handleSubmitAll={handleSubmitAll}
//         />

//     </div>
//   );
// };

// export default MainPage;

// import React, { useEffect, useState } from 'react';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import AddFieldModal from './AddFieldModal';
// import ProjectionField from './Projectionfields';
// import CityDetails from './CityDetails ';
// import { getAllCity } from '../../../FeatureRedux/citydetailReducer/getCityDetail';
// import { useDispatch } from 'react-redux';

// const MainPage = ({}) => {
//   // city , state , country
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [sections, setSections] = useState([]);
//   const [cards, setCards] = useState({});
//   const [cityData, setCityData] = useState({
//     cityname: "",
//     country: "",
//     state: "",
//     latitude: "",
//     longitude: "",
//     imageUrl: "",
//     description: ""
//   });

//   const dispatch = useDispatch();
//   const cityname = "Lucknow"
//   useEffect(()=>{
    
//     const state = "Uttar Pradesh"
//     const country = "India"
//     dispatch(getAllCity({cityname , state  , country}));
//   },[cityname])

//   const openModal = () => setIsModalOpen(true);
//   const closeModal = () => setIsModalOpen(false);

//   const handleAddFields = (selectedFields) => {
//   const newSections = [...sections];
//   const newCards = { ...cards };

//   selectedFields.forEach((field) => {
//     if (!sections.includes(field)) {
//       newSections.push(field);
      
//       // Create a default row for each new section
//       const newCard = { id: Date.now(), isEditing: true };
      
//       switch(field) {
//         case 'History':
//           newCard.title = "";
//           newCard.content = "";
//           newCard.imageUrl = "";
//           break;
//         case 'Transport':
//           newCard.type = "";
//           newCard.details = "";
//           newCard.imageUrl = "";
//           break;
//         case 'Social Impact':
//           newCard.aspect = "";
//           newCard.impact = "";
//           newCard.imageUrl = "";
//           break;
//         case 'Population':
//           newCard.year = "";
//           newCard.count = "";
//           newCard.notes = "";
//           newCard.imageUrl = "";
//           break;
//         case 'Culture':
//           newCard.aspect = "";
//           newCard.details = "";
//           newCard.imageUrl = "";
//           break;
//         case 'Economy':
//           newCard.sector = "";
//           newCard.info = "";
//           newCard.imageUrl = "";
//           break;
//         case 'Technology':
//           newCard.field = "";
//           newCard.description = "";
//           newCard.imageUrl = "";
//           break;
//         case 'Infrastructure':
//           newCard.field = "";
//           newCard.description = "";
//           newCard.imageUrl = "";
//           break;
//         default:
//           break;
//       }
      
//       newCards[field] = [newCard];
//     }
//   });

//   setSections(newSections);
//   setCards(newCards);
//   toast.success('Fields added successfully!');
// };

 

//   const handleSubmitAll = () => {
//     // Validate city data first
//     if (!cityData.cityname || !cityData.country || !cityData.state || !cityData.description) {
//       toast.error('Please fill all required city details');
//       return;
//     }

//     // Validate sections data
//     let hasErrors = false;
//     const newErrors = {};

//     sections.forEach(section => {
//       if (!cards[section] || cards[section].length === 0) {
//         toast.error(`Please add at least one item to ${section} section`);
//         hasErrors = true;
//         return;
//       }

//       cards[section].forEach(item => {
//         if (!item.title && !item.type && !item.aspect && !item.sector && !item.field) {
//           toast.error(`Please fill all required fields in ${section} section`);
//           hasErrors = true;
//         }
//       });
//     });

//     if (hasErrors) return;

//     // Prepare final data structure
//     const finalData = {
//       city: {
//         cityname: cityData.cityname,
//         country: cityData.country,
//         state: cityData.state,
//         latitude: parseFloat(cityData.latitude) || 0,
//         longitude: parseFloat(cityData.longitude) || 0,
//         imageUrl: cityData.imageUrl,
//         description: cityData.description
//       }
//     };

//     // Add all sections to final data
//     sections.forEach(section => {
//       finalData[section.toLowerCase().replace(' ', '')] = cards[section].map(item => {
//         const baseItem = {
//           imageUrl: item.imageUrl || ""
//         };

//         // Add fields based on section type
//         switch(section) {
//           case 'History':
//             return {
//               ...baseItem,
//               title: item.title,
//               content: item.content
//             };
//           case 'Transport':
//             return {
//               ...baseItem,
//               type: item.type,
//               details: item.details
//             };
//           case 'Social Impact':
//             return {
//               ...baseItem,
//               aspect: item.aspect,
//               impact: item.impact
//             };
//           case 'Population':
//             return {
//               ...baseItem,
//               year: parseInt(item.year) || 0,
//               count: parseInt(item.count) || 0,
//               notes: item.notes
//             };
//           case 'Culture':
//             return {
//               ...baseItem,
//               aspect: item.aspect,
//               details: item.details
//             };
//           case 'Economy':
//             return {
//               ...baseItem,
//               sector: item.sector,
//               info: item.info
//             };
//           case 'Technology':
//             return {
//               ...baseItem,
//               field: item.field,
//               description: item.description
//             };
//           case 'Infrastructure':
//             return {
//               ...baseItem,
//               field: item.field,
//               description: item.description
//             };
//           default:
//             return baseItem;
//         }
//       }); 
//     });

//     console.log("Final data:", finalData);
//     toast.success('Data submitted successfully!');
//   };

//   return (
//     <div className="p-6 relative bg-gray-900" style={{
//       overflowY: 'scroll',
//       height: '80vh',
//       width: '100%',
//       scrollbarWidth: 'px',
//       msOverflowStyle: 'none'
//     }}>
//       <ToastContainer position="top-right" autoClose={3000} />
      
//       <AddFieldModal
//         isOpen={isModalOpen}
//         onClose={closeModal}
//         onAddFields={handleAddFields}
//       />

//       <CityDetails 
//         cityData={cityData}
//         setCityData={setCityData}
//       />

//       <ProjectionField 
//         sections={sections}
//         cards={cards}
//         setCards={setCards}
//         setSections={setSections}
//         openModal={openModal}
//         handleSubmitAll={handleSubmitAll}
//       />
//     </div>
//   );
// };

// export default MainPage;

// import React, { useState } from 'react';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import AddFieldModal from './AddFieldModal';
// import ProjectionField from './Projectionfields';

// const MainPage = () => {
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [sections, setSections] = useState([]);
//   const [cards, setCards] = useState({});

//   const openModal = () => setIsModalOpen(true);
//   const closeModal = () => setIsModalOpen(false);

//   const handleAddFields = (selectedFields) => {
//     const newSections = [...sections];
//     const newCards = { ...cards };

//     selectedFields.forEach((field) => {
//       if (!sections.includes(field)) {
//         newSections.push(field);
//         newCards[field] = [{
//           id: Date.now(),
//           name: "",
//           state: "",
//           country: "",
//           city: "",  // Make sure city is included in initial state
//           image: "",
//           description: "",
//           isEditing: true
//         }];
//       }
//     });

//     setSections(newSections);
//     setCards(newCards);
//     toast.success('Fields added successfully!');
//   };

//   return (
//     <div className="p-6 relative bg-gray-900" style={{
//       overflowY: 'scroll',
//       height: '80vh',
//       width: '100%',
//       scrollbarWidth: 'px',
//       msOverflowStyle: 'none'
//     }}>
//       <ToastContainer position="top-right" autoClose={3000} />
      
//       <AddFieldModal
//         isOpen={isModalOpen}
//         onClose={closeModal}
//         onAddFields={handleAddFields}
//       />

//       <ProjectionField 
//         sections={sections}
//         cards={cards}
//         setCards={setCards}
//         setSections={setSections}
//         openModal={openModal}
//       />
//     </div>
//   );
// };

// export default MainPage;